"use client"

import { useState } from "react"
import Link from "next/link"
import { Check, Crown, BookmarkIcon, Sparkles } from "lucide-react"
import { Button } from "@/components/ui/button"

const saverFeatures = [
  "Everything in Free",
  "1 residency profile in directory",
  "1 open call per year",
  "Save & organize residency shortlists",
  "Email deadline reminders",
  "New open call notifications",
]

const proFeatures = [
  "Everything in Saver",
  "Unlimited residency profiles",
  "Unlimited open calls",
  "Apply to residencies in-app",
  "Stored portable artist profile",
  "Application tracker & status",
  "Advanced directory filters (amenities, caregiver, ratings)",
  "Cosmic Match residency finder",
  "Pro badge on profile",
]

export default function PricingSection({ isLoggedIn, currentTier }: { isLoggedIn: boolean; currentTier?: string }) {
  const [annual, setAnnual] = useState(true)

  return (
    <div>
      {/* Interval toggle */}
      <div className="flex items-center justify-center gap-3 mb-10">
        <span className={`text-[13px] font-medium tracking-wide ${!annual ? "text-[var(--foreground)]" : "text-[var(--muted-foreground)]"}`}>
          Monthly
        </span>
        <button
          type="button"
          onClick={() => setAnnual(!annual)}
          className={`relative w-11 h-[22px] rounded-full transition-colors duration-300 ${
            annual ? "bg-[var(--primary)]" : "bg-[var(--muted)]"
          }`}
          aria-label="Toggle annual billing"
        >
          <span
            className={`absolute top-[2px] left-[2px] w-[18px] h-[18px] rounded-full bg-white transition-transform duration-300 ${
              annual ? "translate-x-[18px]" : "translate-x-0"
            }`}
          />
        </button>
        <span className={`text-[13px] font-medium tracking-wide ${annual ? "text-[var(--foreground)]" : "text-[var(--muted-foreground)]"}`}>
          Annual
        </span>
        {annual && (
          <span className="text-[11px] font-semibold tracking-wide text-[var(--accent)] bg-[var(--secondary)] px-2 py-0.5 rounded-md">
            Save 27%
          </span>
        )}
      </div>

      {/* Pricing cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Free */}
        <div className="border border-[var(--border)] rounded-lg p-6 transition-all duration-300 hover:shadow-sm hover:border-[var(--muted)]">
          <div className="overline mb-3">Free</div>
          <div className="text-3xl font-semibold tracking-tight mb-1">$0</div>
          <p className="text-[13px] text-[var(--muted-foreground)] mb-6">Forever free</p>
          <ul className="space-y-3 mb-8">
            {["Browse the full directory", "Read all community reviews", "Write reviews", "1 residency profile (claim or submit)"].map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-[13px]">
                <Check size={13} className="text-[var(--muted-foreground)] flex-shrink-0 mt-0.5" />
                {f}
              </li>
            ))}
          </ul>
          {isLoggedIn ? (
            <Button variant="outline" className="w-full" disabled>
              {currentTier === "free" || !currentTier ? "Current plan" : "Downgrade"}
            </Button>
          ) : (
            <Link href="/register"><Button variant="outline" className="w-full">Get Started</Button></Link>
          )}
        </div>

        {/* Saver */}
        <div className="border border-[var(--border)] rounded-lg p-6 relative transition-all duration-300 hover:shadow-sm hover:border-[var(--muted)]">
          <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
            <span className="bg-[var(--foreground)] text-white text-[10px] font-semibold tracking-wider uppercase px-3 py-1 rounded-md flex items-center gap-1.5">
              <BookmarkIcon size={9} /> Best Value
            </span>
          </div>
          <div className="flex items-center gap-1.5 mb-3">
            <BookmarkIcon size={13} className="text-[var(--foreground)]" />
            <span className="overline">Saver</span>
          </div>
          <div className="flex items-end gap-1 mb-1">
            <span className="text-3xl font-semibold tracking-tight">${annual ? "35" : "4"}</span>
            <span className="text-[var(--muted-foreground)] text-[13px] mb-0.5">/{annual ? "year" : "month"}</span>
          </div>
          <p className="text-[13px] text-[var(--muted-foreground)] mb-6">
            {annual ? (
              <>$2.92/mo</>
            ) : (
              <>Or $35/year</>
            )}
          </p>
          <ul className="space-y-3 mb-8">
            {saverFeatures.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-[13px]">
                <Check size={13} className="text-[var(--accent)] flex-shrink-0 mt-0.5" />
                {f}
              </li>
            ))}
          </ul>
          {currentTier === "saver" ? (
            <Button variant="outline" className="w-full" disabled>Current plan</Button>
          ) : (
            <form action={`/api/stripe/checkout?tier=saver&interval=${annual ? "annual" : "monthly"}`} method="POST">
              <Button type="submit" className="w-full" size="lg">
                {currentTier === "pro" ? "Switch to Saver" : "Start Free Trial"}
              </Button>
            </form>
          )}
          {currentTier !== "saver" && (
            <p className="text-[11px] text-center text-[var(--muted-foreground)] mt-2 leading-relaxed">
              7 days free, then ${annual ? "35/year" : "4/month"}.<br />
              We&apos;ll email you 3 days before it starts. Cancel anytime in one click.
            </p>
          )}
        </div>

        {/* Pro */}
        <div className="border-2 border-[var(--primary)] rounded-lg p-6 relative transition-all duration-300 hover:shadow-md">
          <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
            <span className="bg-[var(--primary)] text-white text-[10px] font-semibold tracking-wider uppercase px-3 py-1 rounded-md flex items-center gap-1.5">
              <Crown size={9} /> Most Popular
            </span>
          </div>
          <div className="flex items-center gap-1.5 mb-3">
            <Crown size={13} className="text-[var(--primary)]" />
            <span className="overline">Pro</span>
          </div>
          <div className="flex items-end gap-1 mb-1">
            <span className="text-3xl font-semibold tracking-tight">${annual ? "79" : "9"}</span>
            <span className="text-[var(--muted-foreground)] text-[13px] mb-0.5">/{annual ? "year" : "month"}</span>
          </div>
          <p className="text-[13px] text-[var(--muted-foreground)] mb-6">
            {annual ? (
              <>$6.58/mo</>
            ) : (
              <>Or $79/year</>
            )}
          </p>
          <ul className="space-y-3 mb-8">
            {proFeatures.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-[13px]">
                <Check size={13} className="text-[var(--primary)] flex-shrink-0 mt-0.5" />
                {f}
              </li>
            ))}
          </ul>
          {currentTier === "pro" ? (
            <Button variant="outline" className="w-full" disabled>Current plan</Button>
          ) : (
            <form action={`/api/stripe/checkout?tier=pro&interval=${annual ? "annual" : "monthly"}`} method="POST">
              <Button type="submit" className="w-full" size="lg">
                Start Free Trial
              </Button>
            </form>
          )}
          {currentTier !== "pro" && (
            <p className="text-[11px] text-center text-[var(--muted-foreground)] mt-2 leading-relaxed">
              7 days free, then ${annual ? "79/year" : "9/month"}.<br />
              We&apos;ll email you 3 days before it starts. Cancel anytime in one click.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
